import React, { useState, useEffect } from 'react';
import Header from '@/components/shared/Header';
import BottomNav from '@/components/shared/BottomNav';
import LoadingSpinner from '@/components/shared/LoadingSpinner';
import BudgetCategoryRow from './BudgetCategoryRow';
import PullToRefresh from '@/components/shared/PullToRefresh';
import { useBudget } from '@/hooks/useBudget';
import { useAppContext } from '@/context/AppContext';
import { formatCurrency, currentMonth, formatMonthYear } from '@/utils/formatters';

export default function BudgetScreen() {
  const { selectedMonth, setSelectedMonth, categories } = useAppContext();
  const { budget, categoryStatus, totalSpent, loading, save, refetch } = useBudget(selectedMonth);

  const [totalBudget, setTotalBudget] = useState('');
  const [catBudgets, setCatBudgets]   = useState({});
  const [saving, setSaving]           = useState(false);

  // Populate form when budget loads
  useEffect(() => {
    if (budget) {
      setTotalBudget(String(budget.totalBudget ?? ''));
      setCatBudgets(Object.fromEntries(
        Object.entries(budget.categories ?? {}).map(([k, v]) => [k, String(v)])
      ));
    }
  }, [budget]);

  function setCat(catId, value) {
    setCatBudgets(prev => ({ ...prev, [catId]: value }));
  }

  // Auto-calculate total budget as sum of all category budgets
  useEffect(() => {
    const sum = Object.values(catBudgets)
      .map(v => Number(v))
      .filter(n => !isNaN(n) && n > 0)
      .reduce((a, b) => a + b, 0);
    if (sum > 0) setTotalBudget(String(sum));
  }, [catBudgets]);

  async function handleSave() {
    setSaving(true);
    try {
      const catNums = Object.fromEntries(
        Object.entries(catBudgets)
          .filter(([, v]) => v && !isNaN(Number(v)))
          .map(([k, v]) => [k, Number(v)])
      );
      await save({ totalBudget: Number(totalBudget), categories: catNums });
    } finally {
      setSaving(false);
    }
  }

  const monthLabel = formatMonthYear(new Date(`${selectedMonth}-01`));

  return (
    <div className="flex flex-col h-full">
      <Header
        title="💰 Monthly Budget"
        bgColor="bg-purple"
        subtitle={monthLabel}
        rightActions={
          <input
            type="month"
            value={selectedMonth}
            max={currentMonth()}
            onChange={e => setSelectedMonth(e.target.value)}
            className="text-xs bg-purple-800 text-white border border-purple-300 rounded px-2 py-1"
            aria-label="Select month"
          />
        }
      />

      <PullToRefresh onRefresh={refetch} className="flex-1 overflow-y-auto scrollbar-none pb-20">
        {/* Overview card */}
        <div className="m-3 rounded-card p-4 text-white"
          style={{ background: 'linear-gradient(135deg, #6A1B9A, #4A148C)' }}>
          <div className="flex justify-around">
            {(() => {
              const rem = (budget?.totalBudget ?? 0) - totalSpent;
              const isOver = rem < 0;
              return [
                { label: 'Budget Set', value: formatCurrency(budget?.totalBudget ?? 0) },
                { label: 'Spent',      value: formatCurrency(totalSpent) },
                { label: isOver ? 'Over Budget' : 'Remaining',
                  value: isOver ? `-${formatCurrency(Math.abs(rem))}` : formatCurrency(rem),
                  colorClass: isOver ? 'text-red-300' : 'text-green-300' },
              ];
            })().map(({ label, value, colorClass }) => (
              <div key={label} className="text-center">
                <p className="text-xs opacity-70 uppercase tracking-wide">{label}</p>
                <p className={`text-base font-bold mt-1 ${colorClass ?? ''}`}>{value}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Total budget input */}
        <div className="mx-3 mb-3 card">
          <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Total Monthly Budget (₹)</label>
          <div className="flex items-center gap-2 border border-gray-300 rounded-input px-3 py-2 bg-gray-50">
            <span className="text-purple font-bold">₹</span>
            <input
              type="number"
              value={totalBudget}
              readOnly
              className="flex-1 outline-none text-xl font-bold text-gray-800 bg-transparent cursor-default"
              placeholder="0"
              aria-label="Total monthly budget (auto-calculated)"
            />
          </div>
          <p className="text-xs text-gray-400 mt-1">Auto-calculated as sum of category budgets below</p>
        </div>

        <h2 className="text-xs font-bold uppercase tracking-wide text-gray-600 px-3 mb-2">
          Category Status
        </h2>

        {loading ? <LoadingSpinner /> : (
          <div className="px-3 space-y-2">
            {categories.map(cat => (
              <BudgetCategoryRow
                key={cat.id}
                category={cat}
                budgetValue={catBudgets[cat.id] ?? ''}
                onBudgetChange={v => setCat(cat.id, v)}
                status={categoryStatus[cat.id]}
              />
            ))}
          </div>
        )}

        <div className="px-3 mt-4">
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn-primary w-full py-3 disabled:opacity-60"
          >
            {saving ? 'Saving…' : '💾 Save Budget'}
          </button>
        </div>
      </PullToRefresh>

      <BottomNav />
    </div>
  );
}
